import React, { useCallback, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { Loading } from "go-core/components/Loading";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import { DeliveryEmployeeApi } from "../../../../../../../../services/Api/Organization/types";
import { DeliveryEmployeeFilter, DeliveryEmployeeFilterId, LiveOrdersFilters } from "../../services/types";
import {
	getDeliveryEmployeeDefaultSelectedOption,
	getDeliveryEmployeeInputValueBasedOnFilterState,
	getDeliveryEmployeesSelectOptions,
	getDeliveryFilterOptionTranslation,
	inputCustomStyles,
} from "./utils";

interface Props {
	innerFilters: LiveOrdersFilters;
	setInnerFilters: (filters: LiveOrdersFilters) => void;
}

const LiveOrdersDeliveryEmployeeFilter = ({ innerFilters, setInnerFilters }: Props) => {
	const [selectedOption, setSelectedOption] = useState<DeliveryEmployeeFilterId | undefined>(() =>
		getDeliveryEmployeeDefaultSelectedOption(innerFilters)
	);
	const { t } = useTranslation();
	const [deliveryEmployeesLoading, setDeliveryEmployeesLoading] = useState(false);
	const [deliveryEmployees, setDeliveryEmployees] = useState<DeliveryEmployeeApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const selectedInputValue = getDeliveryEmployeeInputValueBasedOnFilterState(
		innerFilters.deliveryEmployee,
		t,
		selectedOption,
		deliveryEmployees
	);
	const driversOptions = getDeliveryEmployeesSelectOptions(t, selectedOption, deliveryEmployees);

	const handleFetchDeliveryEmployees = useCallback(async () => {
		setDeliveryEmployeesLoading(true);
		try {
			const res = await apiOrganization.getDeliveryEmployees({ size: 0, include: "employee" });
			setDeliveryEmployees(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setDeliveryEmployeesLoading(false);
	}, [addFlash]);

	useEffect(() => {
		if ((selectedOption === "with_driver" || selectedOption === "all") && !deliveryEmployees) {
			handleFetchDeliveryEmployees();
		}
	}, [handleFetchDeliveryEmployees, selectedOption, deliveryEmployees]);

	const onSelectChange = (value: { id: string; label: string } | null) => {
		if (!value || !innerFilters.deliveryEmployee) return;

		setInnerFilters({
			...innerFilters,
			deliveryEmployee: {
				...innerFilters.deliveryEmployee,
				employeeId: value.id,
			},
		});
	};

	const handleOnButtonClick = async (type: DeliveryEmployeeFilterId) => {
		if (selectedOption === type) {
			setSelectedOption(undefined);
			setInnerFilters({
				...innerFilters,
				deliveryEmployee: null,
			});
		} else {
			const employeeFilterValue: DeliveryEmployeeFilter = {
				id: type,
				label: getDeliveryFilterOptionTranslation(type, t),
				employeeId: type,
			};

			setInnerFilters({
				...innerFilters,
				deliveryEmployee: employeeFilterValue,
			});
			setSelectedOption(type);
		}
	};

	return (
		<div className="mt-2">
			<Form.Label>{t("modules.live_order.field.orders.title")}</Form.Label>
			<div className="d-flex flex-wrap gap-2">
				<Button
					className="p-2"
					variant={selectedOption === "all" ? "primary" : "light"}
					onClick={() => handleOnButtonClick("all")}
				>
					{getDeliveryFilterOptionTranslation("all", t)}
				</Button>
				<Button
					className="p-2"
					onClick={() => handleOnButtonClick("with_driver")}
					variant={selectedOption === "with_driver" ? "primary" : "light"}
				>
					{getDeliveryFilterOptionTranslation("with_driver", t)}
				</Button>
				<Button
					className="p-2"
					onClick={() => handleOnButtonClick("without_driver")}
					variant={selectedOption === "without_driver" ? "primary" : "light"}
				>
					{getDeliveryFilterOptionTranslation("without_driver", t)}
				</Button>
			</div>
			{(selectedOption === "with_driver" || selectedOption === "all") && (
				<div className="mt-2">
					{deliveryEmployeesLoading ? (
						<Loading />
					) : (
						<>
							<Form.Label>{t("common.word.driver")}</Form.Label>
							<Select
								styles={inputCustomStyles}
								className="form-select"
								value={selectedInputValue}
								defaultValue={selectedInputValue}
								onChange={onSelectChange}
								getOptionValue={(opt) => opt.id}
								options={driversOptions}
								placeholder={t("lib:common.action.select")}
							/>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default LiveOrdersDeliveryEmployeeFilter;
